<template>
  <figure
    class="oct-media"
    :class="{
      'oct-media--natural': natural
    }"
    :aria-selected="selected"
  >
    <picture
      class="oct-media__picture"
      :class="{
        'oct-media__picture--empty': !$_octMedia_image.lowResolution
      }"
    >
      <source
        :srcset="getSafeImage($_octMedia_image.standardResolution)"
        :media="`(min-width: ${breakPoint}px)`"
      />

      <img
        class="oct-media__image"
        :src="getSafeImage($_octMedia_image.lowResolution)"
        :alt="alt"
      />

      <oct-icon-button
        class="oct-media__icon--close"
        icon="close"
        type="on-primary"
        size="minimum"
        v-if="removable"
        @click.stop.native="$emit('remove')"
      />

      <oct-icon-button
        class="oct-media__icon--lock"
        icon="lock"
        type="on-primary"
        size="minimum"
        v-if="lock"
      />
    </picture>

    <slot />
  </figure>
</template>

<script>
import OctImage from '@/components/image/OctImage.js'
import OctIconButton from '@/components/icon-button/OctIconButton.vue';

export default {
  name: 'OctMedia',
  components: { OctIconButton },
  props: {
    /** Image */
    image: {
      type: Object,
      default: () => OctImage()
    },
    /** Alt */
    alt: {
      type: String,
      default: ''
    },
    /** Indicating the media is selected */
    selected: {
      type: Boolean,
      default: false
    },
    /** Removable */
    removable: {
      type: Boolean,
      default: false
    },
    /** Lock */
    lock: {
      type: Boolean,
      default: false
    },
    /** Break point */
    breakPoint: {
      type: Number,
      default: 769
    },
    /** Use natural size */
    natural: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    /** Check src and return safe path */
    getSafeImage (src) {
      return src || 'data:image/gif;base64,R0lGODlhAQABAGAAACH5BAEKAP8ALAAAAAABAAEAAAgEAP8FBAA7'
    }
  },
  computed: {
    $_octMedia_image () {
      return Object.assign({
        lowResolution: 'data:image/gif;base64,R0lGODlhAQABAGAAACH5BAEKAP8ALAAAAAABAAEAAAgEAP8FBAA7',
        standardResolution: 'data:image/gif;base64,R0lGODlhAQABAGAAACH5BAEKAP8ALAAAAAABAAEAAAgEAP8FBAA7',
      }, this.image || {})
    }
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-media {
  position: relative;
  margin: 0;
  width: 100%;
  overflow: hidden;
  border-radius: oct-rem(8);
  aspect-ratio: 1;

  &[aria-selected="true"] {
    border: oct-rem(2) solid #000;
  }

  &--natural {
    padding-top: 0;
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    background-color: #000;
    z-index: 1;
    opacity: 0;
    transition-duration: .2s;
  }

  &:hover:after {
    opacity: .05;
  }

  .oct-icon-button {
    z-index: 2;
  }

@media print, screen and (min-width: map-get($grid-breakpoints, 'md')) {
  border-radius: oct-rem(16);
}
}

.oct-media__picture {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;

  &--empty {
    background:url("data:image/svg+xml;charset=utf-8,%3csvg version='1.1' xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 2 2' preserveAspectRatio='none'%3e%3cg transform='matrix(-1 0 0-1 2 2)'%3e%3cpath fill='none' stroke='%23000' stroke-width='1' vector-effect='non-scaling-stroke' opacity='.2' d='M1-1-2 2M3-1l-4 4M3-1l-4 4M4 0l-4 4M3 1 1 3'/%3e%3c/g%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
  }

  .oct-media--natural & {
    position: static;
  }
}

.oct-media__image {
  position: absolute;
  margin: auto;
  width: 100%;
  height: 100%;
  object-fit: cover;

  .oct-media--natural & {
    position: static;
    display: block;
    height: auto;
    object-fit: initial;
  }
}

.oct-media__icon--close {
  position: absolute;
  top: oct-rem(12);
  left: oct-rem(12);
}

.oct-media__icon--lock {
  position: absolute;
  right: oct-rem(12);
  bottom: oct-rem(12);
  pointer-events: none;
  background-color: rgba(255,255,255,.8);
}
</style>
